import React from 'react';
import { Eye, GitPullRequest, Zap } from 'lucide-react';
import { Button, EntityPicker, Toggle } from '../../../components/ui';
import type { EntityPickerOption } from '../../../components/ui';
import { Label } from '../../../components/ui';
import { RuntimePolicyPicker } from '../../../components/ui/RuntimePolicyPicker.js';
import { RuntimePolicySurface } from '../../../generated/api/types.gen.js';
import { FieldError, useFieldInvalid } from '../../../components/entity/FieldError.js';
import { listTemplates } from '../../../generated/api';
import type { WizardState } from './hire-types.js';

interface HireStepQualificationsProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
  /** Runtime policy for the new employee; null = the workforce default. Held outside the zod form. */
  runtimePolicyId: number | null;
  onRuntimePolicyChange: (policyId: number) => void;
}

const PERMISSION_TIERS = [
  {
    value: 'observer',
    label: 'Observer',
    description: 'Can produce findings, docs, and alerts',
    icon: Eye,
    colorClass: 'text-accent-text border-accent/30 bg-accent/10',
  },
  {
    value: 'contributor',
    label: 'Contributor',
    description: 'Observer + can submit PRs and code changes',
    icon: GitPullRequest,
    colorClass: 'text-warning border-warning/30 bg-warning-muted',
  },
  {
    value: 'operator',
    label: 'Operator',
    description: 'Contributor + external API calls and system actions',
    icon: Zap,
    colorClass: 'text-danger border-danger/30 bg-danger-muted',
  },
] as const;

const loadTemplateOptions = async (): Promise<EntityPickerOption[]> => {
  const res = await listTemplates();
  const items = (res.data?.data ?? []) as Array<{
    id: number;
    title: string;
    slug: string;
    departmentId: string | null;
    goal: string | null;
  }>;
  return items.map((t) => ({
    id: t.id,
    label: t.title,
    description: [t.departmentId, t.goal].filter(Boolean).join(' · ') || t.slug,
  }));
};

export const HireStepQualifications: React.FC<HireStepQualificationsProps> = ({
  state,
  onChange,
  runtimePolicyId,
  onRuntimePolicyChange,
}) => {
  const templateInvalid = useFieldInvalid('templateId');
  const permissionInvalid = useFieldInvalid('permissionTier');

  return (
    <div className="space-y-4">
      <div>
        <Label hint="optional">Template</Label>
        <EntityPicker
          value={state.templateId ?? null}
          onChange={(id) => onChange({ templateId: id })}
          queryKey={['templates']}
          loadOptions={loadTemplateOptions}
          placeholder="Search templates..."
          emptyMessage="No templates available"
          invalid={templateInvalid}
          ariaLabel="Template"
        />
        <FieldError name="templateId" />
        <p className="text-xs text-faint mt-1">The template determines available skills and system prompts.</p>
      </div>

      <div>
        <Label>Permission Tier</Label>
        <div className={`grid grid-cols-3 gap-2 ${permissionInvalid ? 'p-1 rounded-lg border border-danger' : ''}`}>
          {PERMISSION_TIERS.map((tier) => {
            const Icon = tier.icon;
            const selected = state.permissionTier === tier.value;
            return (
              <Button
                key={tier.value}
                variant="ghost"
                aria-pressed={selected}
                onClick={() => onChange({ permissionTier: tier.value })}
                className={`h-auto flex-col gap-1.5 whitespace-normal p-3 text-center ${
                  selected ? `${tier.colorClass} border-2` : 'bg-surface-alt border hover:border-strong'
                }`}
              >
                <Icon size={20} className={selected ? '' : 'text-muted'} />
                <span className={`text-sm font-medium ${selected ? '' : 'text-body'}`}>{tier.label}</span>
                <span className={`text-xs leading-tight ${selected ? 'opacity-80' : 'text-faint'}`}>
                  {tier.description}
                </span>
              </Button>
            );
          })}
        </div>
        <FieldError name="permissionTier" />
      </div>

      <div>
        <Label>Runtime policy</Label>
        <RuntimePolicyPicker
          surface={RuntimePolicySurface.WORKFORCE}
          value={runtimePolicyId}
          onChange={onRuntimePolicyChange}
        />
        <p className="text-xs text-faint mt-1">Decides which models this employee runs on and under what limits.</p>
      </div>

      <div className="pt-2">
        <Toggle
          checked={state.requiresApproval}
          onChange={(checked) => onChange({ requiresApproval: checked })}
          label="Requires human approval before actions take effect"
        />
      </div>
    </div>
  );
};
